import { surfaceFor } from './color';

/**
 * A demo's style preset (`theme.style`): how its popups, buttons and cards
 * look. Pure data, no zod — the player, the MP4 exporter and Studio's preview
 * all read it, so they agree on one demo (same reason as `surfaceFor`).
 *
 *   - `mado`    mado's own landing-page look: cream paper, ink outline, hard
 *               offset shadow. What every document published before this field
 *               existed renders, so absent means `mado` and nothing stored changes.
 *   - `neutral` plain and brand-free: a dark tip, white cards, hairline edges,
 *               soft shadows, small corners. Works for any product.
 *   - `brand`   the customer's own look: surfaces from the demo's background,
 *               hairline edges, soft shadows, corner radius from `theme.radius`
 *               (read off their site), the accent on buttons and markers.
 *
 * Fonts and colours (accent/background/font) stay their own theme fields; a
 * style only decides the furniture around them.
 */
export const DEMO_STYLES = ['mado', 'neutral', 'brand'] as const;
export type DemoStyle = (typeof DEMO_STYLES)[number];

/** Corner radius (px) the plain styles use when `theme.radius` is absent. */
export const DEFAULT_RADIUS = 8;
export const MIN_RADIUS = 0;
export const MAX_RADIUS = 24;

export interface ResolvedStyle {
  id: DemoStyle;
  /** Card / popup fill, text on it, secondary text, and the eyebrow colour. */
  paper: string;
  ink: string;
  dim: string;
  eyebrow: string;
  /** Outline width (px) and colour on tip, cards and buttons. */
  edgeW: number;
  edge: string;
  /** The step tip: its own fill/text/outline, since `neutral` inverts it. */
  tipBg: string;
  tipFg: string;
  tipEdge: string;
  /** Hard offset shadow depth (px) under tip / card / button; 0 = soft only. */
  hardTip: number;
  hardCard: number;
  hardBtn: number;
  /** Corner radii (px). */
  rTip: number;
  rCard: number;
  rEnd: number;
  rBtn: number;
  rPop: number;
  rMark: number;
  /** Draw mado's four-pane tile in the tip. Only the mado style does. */
  mark: boolean;
}

export interface StyleInput {
  style?: DemoStyle | undefined;
  background: string;
  radius?: number | undefined;
}

function clampRadius(r: number | undefined): number {
  if (r === undefined || !Number.isFinite(r)) return DEFAULT_RADIUS;
  return Math.max(MIN_RADIUS, Math.min(MAX_RADIUS, Math.round(r)));
}

export function resolveStyle(input: StyleInput): ResolvedStyle {
  const id: DemoStyle = input.style ?? 'mado';
  if (id === 'mado') {
    return {
      id,
      paper: '#FFFAF2',
      ink: '#3A2114',
      dim: '#7A5A45',
      eyebrow: '#9C5019',
      edgeW: 2,
      edge: '#3A2114',
      tipBg: '#FFFAF2',
      tipFg: '#3A2114',
      tipEdge: '#3A2114',
      hardTip: 4,
      hardCard: 6,
      hardBtn: 4,
      rTip: 16,
      rCard: 26,
      rEnd: 20,
      rBtn: 15,
      rPop: 14,
      rMark: 13,
      mark: true,
    };
  }
  const r = clampRadius(input.radius);
  const common = {
    id,
    edgeW: 1,
    hardTip: 0,
    hardCard: 0,
    hardBtn: 0,
    rTip: r,
    rCard: r + 4,
    rEnd: r + 4,
    rBtn: r,
    rPop: r,
    rMark: r,
    mark: false,
  };
  if (id === 'neutral') {
    return {
      ...common,
      paper: '#FFFFFF',
      ink: '#1F2328',
      dim: '#5B6068',
      eyebrow: '#5B6068',
      edge: '#D9D8D3',
      tipBg: '#1F2328',
      tipFg: '#F3F4F6',
      tipEdge: '#1F2328',
      // Neutral keeps its small, quiet corners whatever the radius says.
      rTip: 8,
      rCard: 12,
      rEnd: 12,
      rBtn: 8,
      rPop: 8,
      rMark: 8,
    };
  }
  const s = surfaceFor(input.background);
  return {
    ...common,
    paper: s.surface,
    ink: s.fg,
    dim: s.fgDim,
    eyebrow: s.fgDim,
    edge: s.border,
    tipBg: s.surface,
    tipFg: s.fg,
    tipEdge: s.border,
  };
}
